import React, { useEffect, useState, useRef } from "react";
import type { WordModel } from '../../type/word';
import type { UserModel } from "../../type/user";
import { addWord, deleteWord, updateWord, moveWord } from '././wordActions';
import { loadWords } from './dataLoaders';
import { getUser, isAuthenticated } from "../../auth/auth";
import { useSpinner } from "../../components/spinner/spinner";
import { useToast } from "../../components/toast/toast";
import { useLanguage } from "../../context/languageContext";
import WordModal from "../../modal/addWords/addWordsModal";
import PageHeader from "../../components/pageHeader/pageHeader";
import EmptyState from "../../components/emptyState/emptyState";
import WordRow from "./wordRow";
import Checkbox from "../../components/checkbox/checkbox";
import ConfirmDialog from "../../modal/confirmDialog/confirmDialog";
import "./words.scss";

type WordColumn = keyof WordModel;

const Dictionary: React.FC = () => {
  const [allWords, setAllWords] = useState<WordModel[]>([]);
  const [filteredWords, setFilteredWords] = useState<WordModel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [dictionaries, setDictionaries] = useState<{ iddictionary: number; name: string }[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [user, setUser] = useState<UserModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const {showSpinner, hideSpinner} = useSpinner();
  const {showToast} = useToast();
  const { languages, selectedLanguageId } = useLanguage();
  const selectedLanguage = languages.find(l => l.idlanguage === selectedLanguageId);
  const langCode = selectedLanguage?.code;
  const langName = selectedLanguage?.name;
  const [selectedWordId, setSelectedWordId] = useState<number | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedWordIds, setSelectedWordIds] = useState<Set<number>>(new Set());
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  const [currentSort, setCurrentSort] = useState<{ column: WordColumn; order: "asc" | "desc" }>({
    column: "eng",
    order: "asc",
  });

  const [iddictionary, setIddictionary] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("iddictionary");
  });
 
 
  // Update iddictionary when URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newIddictionary = params.get("iddictionary");
    setIddictionary(newIddictionary);
  }, [window.location.search]);

  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      setUser(userData);
    }
  }, []);

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const cached = localStorage.getItem("app_dictionaries");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setDictionaries(parsed);
      }
    } catch {/* ignore storage errors */}

    const onUpdated = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      if (Array.isArray(detail)) setDictionaries(detail);
    };
    document.addEventListener("dictionaries:updated", onUpdated as EventListener);

    //loadDictionaries(setDictionaries);

    return () => document.removeEventListener("dictionaries:updated", onUpdated as EventListener);
  }, []);


  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    const filtered = allWords.filter(w => {
      const eng = (w.eng || "").toLowerCase();
      const pl = (w.pl || "").toLowerCase();
      if (tokens.length === 0) return true;
      return tokens.every(token => eng.includes(token) || pl.includes(token));
    });

    const sorted = sortWords(filtered, currentSort.column, currentSort.order);
    setFilteredWords(sorted);
    resetAndLoadInitial(sorted);
  }, [searchQuery, allWords, currentSort.column, currentSort.order]);

  useEffect(() => {
    if (user !== null) {
      handleLoadWords();
    }
  }, [user, iddictionary, dictionaries]);

  const selectWord = (id: number) => {
    setSelectedWordId(id);
  };

  const remainingExpressions = () => {
    if (!user) return 0;
    //user.max_expressions = 5;
    if (user.max_expressions === -1) return Infinity; // unlimited
    return Math.max((user?.max_expressions ?? 0) - allWords.length, 0);
  };
  const canAddWord = () => remainingExpressions() > 0;  

  const handleLoadWords = () => {
    setIsLoading(true);
    loadWords(
      iddictionary,
      user ? String(user.iduser) : null,
      (words) => {
        setAllWords(words);
        setIsLoading(false);
      },
      setFilteredWords,
      showSpinner,
      hideSpinner,
      sortWords,
      currentSort,
      tableContainerRef,
      dictionaries.map(d => d.iddictionary)
    );
  };

  const sortWords = (words: WordModel[], column: WordColumn, order: "asc" | "desc") => {
    const getField = (w: WordModel) => {
      const v = w[column];
      return (v === undefined || v === null) ? "" : String(v).toLowerCase();
    };
    words.sort((a, b) => {
      const av = getField(a);
      const bv = getField(b);
      const cmp = av.localeCompare(bv);
      return (order === "asc" ? cmp : -cmp);
    });
    return words;
  };

  const resetAndLoadInitial = (words: WordModel[]) => {
    setFilteredWords(words);
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  };

  const toggleSort = (column: WordColumn) => {
    const newOrder: "asc" | "desc" =
      currentSort.column === column && currentSort.order === "asc" ? "desc" : "asc";
    const newSort = { column, order: newOrder };
    setCurrentSort(newSort);

    const sorted = sortWords([...filteredWords], newSort.column, newSort.order);
    setFilteredWords(sorted);
    resetAndLoadInitial(sorted);
  };

  const handleAddNewWord = () => {
    setShowModal(true);
  };

  const handleSaveWord = async (eng: string, pl: string) => {
    await addWord(eng, pl, iddictionary, showSpinner, hideSpinner, showToast, (newWord) => {
      setAllWords(prev => [newWord, ...prev]);  
      setSelectedWordId(newWord.idexpression!);
      //setShowModal(false);
    });
  };

  const handleDeleteWord = async (id: number) => {
    await deleteWord(id, setAllWords, setFilteredWords, showSpinner, hideSpinner, showToast);
  };

  const handleUpdateWord = async (id: number, eng: string, pl: string) => {
    await updateWord(id, eng, pl, allWords, filteredWords, setAllWords, setFilteredWords, sortWords, showSpinner, hideSpinner, showToast, currentSort);
  };

  const handleMoveWord = async (wordId: number, targetDictId: number) => {
    await moveWord(wordId, targetDictId, showSpinner, hideSpinner, showToast, handleLoadWords);
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedWordIds(new Set());
    setShowActionsMenu(false);
  };

  const handleSelectAll = () => {
    const allSelected = filteredWords.length > 0 && filteredWords.every(w => selectedWordIds.has(w.idexpression!));
    if (allSelected) {
      setSelectedWordIds(new Set());
    } else {
      setSelectedWordIds(new Set(filteredWords.map(w => w.idexpression!)));
    }
  };

  const toggleWordSelection = (wordId: number) => {
    setSelectedWordIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wordId)) {
        newSet.delete(wordId);
      } else {
        newSet.add(wordId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedWordIds.size === 0) return;
    setShowActionsMenu(false);
    setShowBulkConfirmDialog(true);
  };

  const handleBulkDeleteConfirmed = async () => {
    setShowBulkConfirmDialog(false);
    for (const id of Array.from(selectedWordIds)) {
      await handleDeleteWord(id);
    }
    setSelectedWordIds(new Set());
  };

  const handleBulkMove = async (targetDictId: number) => {
    if (selectedWordIds.size === 0) return;
    
    for (const id of Array.from(selectedWordIds)) {
      await handleMoveWord(id, targetDictId);
    }
    setSelectedWordIds(new Set());
    setShowActionsMenu(false);
  };

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActionsMenu]);

  const sortArrow = (column: WordColumn) => {
    if (currentSort.column !== column) return null;
    return currentSort.order === "asc" ? " ▲" : " ▼";
  };
  
  const getDictionaryName = () => {
    if (!iddictionary) return "All Expressions";
    const dict = dictionaries.find(d => d.iddictionary === parseInt(iddictionary));
    return dict?.name || "Expressions ";
  };

  return (
    <div className="words-container">
      <PageHeader
        title={getDictionaryName()}
        icon="bi-book"
        addButton={
          iddictionary != null && user?.parameters?.addnewwords_visible.value
            ? {
                label: "New Expression",
                onClick: handleAddNewWord,
                disabled: !canAddWord(),
                variant: !canAddWord() ? 'danger' : 'primary',
                title: canAddWord() 
                  ? "Add new expression" 
                  : `Expression limit reached (${allWords.length}/${user?.max_expressions} used)`,
              }
            : undefined
        }
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: `Search ${langName || "English"} or Polish...`,
        }}
      />

      <div className="card">
        <div className="words-card-body p-0 words-table-container" ref={tableContainerRef}>
          {!isLoading && filteredWords.length === 0 ? (
            searchQuery.trim() && allWords.length > 0 ? (
              <EmptyState
                icon="bi bi-search"
                title="No expressions found"
                description={`No expressions match "${searchQuery}"`}
                button={{
                  label: 'Clear search',
                  onClick: () => setSearchQuery(''),
                  variant: 'outline-primary'
                }}
              />
            ) : allWords.length === 0 ? (
              <EmptyState
                icon="bi bi-book-half"
                title="No expressions yet"
                description={
                  iddictionary 
                    ? "Add your first expression to this dictionary!"
                    : "Start by creating a dictionary and adding expressions."
                }
                button={
                  iddictionary && canAddWord() && user?.parameters?.addnewwords_visible.value
                    ? {
                        label: 'New Expression',
                        onClick: handleAddNewWord,
                        icon: 'bi bi-plus-lg'
                      }
                    : undefined
                }
              />
            ) : null
          ) : (
            <>
              {!isMobile && (
                <div className={`words-row-header ${selectMode ? 'select-mode' : ''}`}>
                  {selectMode && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '6px' }}>
                      <input
                        type="checkbox"
                        checked={filteredWords.length > 0 && filteredWords.every(w => selectedWordIds.has(w.idexpression!))}
                        onChange={handleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }}
                        title="Select / unselect all"
                      />                      
                    </div>
                  )}
                  <div style={{ paddingLeft: '6px' }}>#</div>
                  <div>
                    <span onClick={() => toggleSort("eng")}>{langName || "English"}{sortArrow("eng")}</span>
                  </div>
                  <div>
                    <span onClick={() => toggleSort("pl")}>Polish{sortArrow("pl")}</span>
                  </div>
                  {user?.parameters?.editbuttons_visible?.value && (
                    <div className="words-row-header-action-menu">
                      {selectMode && selectedWordIds.size > 0 && (
                        <div className="words-row-header-action-menu-container" ref={actionsMenuRef}>
                          <button
                            className="btn btn-sm btn-outline-primary btn-actions-menu"
                            onClick={() => setShowActionsMenu(!showActionsMenu)}
                            title="Actions"
                            style={{ padding: '0.1rem 0.5rem' }}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          {showActionsMenu && (
                            <div className="dropdown-menu show words-row-header-action-menu-button">
                              <div className="dropdown-header">Move to Dictionary</div>
                              {dictionaries.length === 0 ? (
                                <div className="dropdown-item disabled">No dictionaries</div>
                              ) : (
                                dictionaries.map(d => (
                                  <button
                                    key={d.iddictionary}
                                    className="dropdown-item"
                                    onClick={() => handleBulkMove(d.iddictionary)}
                                  >
                                    {d.name}
                                  </button>
                                ))
                              )}
                              <div className="dropdown-divider"></div>
                              <button
                                className="dropdown-item text-danger words-row-header-action-menu-delete"
                                onClick={handleBulkDelete}
                              >
                                <i className="bi bi-trash"></i> Delete Selected ({selectedWordIds.size})
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      Edit
                      <Checkbox
                        label=""
                        description=""
                        checked={selectMode}
                        onChange={toggleSelectMode}
                        variant="compact"
                        className="words-select-mode-checkbox"
                      />
                    </div>
                  )}
                </div>
              )}

              {filteredWords.map((word, index) => (
                <WordRow
                  key={word.idexpression}
                  word={word}
                  index={index}
                  updateWord={handleUpdateWord}
                  deleteWord={handleDeleteWord}
                  dictionaries={dictionaries}
                  moveWord={handleMoveWord}
                  isMobile={isMobile}
                  parameters={user?.parameters}
                  selectedWordId={selectedWordId}
                  selectWord={selectWord}
                  selectMode={selectMode}
                  isSelected={selectedWordIds.has(word.idexpression!)}
                  onToggleSelect={() => toggleWordSelection(word.idexpression!)}
                  langCode={langCode}
                  langName={langName}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <WordModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSaveWord}
        canAddWord={canAddWord()}
        remainingExpressions={remainingExpressions()}
        maxExpressions={user?.max_expressions ?? 0}
        packageName={user?.package_name}
        langName={langName}
      />

      <ConfirmDialog
        isOpen={showBulkConfirmDialog}
        title="Confirm Deletion"
        message={`Delete ${selectedWordIds.size} selected expression${selectedWordIds.size !== 1 ? 's' : ''}?`}
        onConfirm={handleBulkDeleteConfirmed}
        onCancel={() => setShowBulkConfirmDialog(false)}
      />
    </div>
  );
};

export default Dictionary;